import { Injectable } from '@angular/core';
import { ElementClasses, FlagOption } from '../models/flags';
import { ColorAnimationService } from './color-animation.service';

@Injectable({
    providedIn: 'root',
})
export class ColorDeciderService {
    private defaultColors: {
        [key: string]: {
            color: string;
            systemColorId: string;
        };
    };

    currentFills: Map<string, string> = new Map();
    cachedColorMap: Map<string, string> = new Map();

    flashingAlarmsMap: Map<string, string> = new Map();
    alarmColors: { [key: string]: { color: string } } = {};

    private readonly GRAPH_UNDEFINED_COLOR = '#9cb2fb';

    constructor(private colorAnimationService: ColorAnimationService) {}

    setDefaultColors(colors) {
        this.defaultColors = colors;
        for (const key in this.defaultColors) {
            const entry = this.defaultColors[key];
            if (entry?.systemColorId) {
                this.cachedColorMap.set(entry.systemColorId, entry.color);
            }
        }
    }

    getColorRanges(ranges: any[], channelValue: number) {
        let color;
        for (let i = 0; i < ranges?.length; i++) {
            const range = ranges[i];
            if (range.enabled && channelValue > range.min && channelValue < range.max) {
                color = this.cachedColorMap.get(range.colorId);
                break;
            }
        }
        return color;
    }

    stopAlarmFlagsColor(elementId) {
        if (this.flashingAlarmsMap.has(elementId)) {
            this.colorAnimationService.removeFlashingSvg(elementId);
            this.colorAnimationService.removeFlashingController(elementId);
            this.flashingAlarmsMap.delete(elementId);
        }
    }

    setAlarmFlagsColor(elementClasses: ElementClasses, element, barFiller?, statusIndicator?, valueInput?) {
        let color;
        const elementId = element.getAttribute('id');
        if (!elementClasses['flag-unack']) {
            this.stopAlarmFlagsColor(elementId);
            if (elementClasses['flag-cancel']) {
                color = this.defaultColors[DefaultColorName.CANCEL].color;
            } else if (elementClasses['flag-alarm']) {
                color = this.defaultColors[DefaultColorName.ALARM].color;
            } else if (elementClasses['flag-caution']) {
                color = this.defaultColors[DefaultColorName.CAUTION].color;
            } else if (elementClasses['flag-warning']) {
                color = this.defaultColors[DefaultColorName.WARNING].color;
            }
            if (this.currentFills.get(elementId) !== color) {
                this.currentFills.set(elementId, color);
                if (!barFiller && !statusIndicator && !valueInput) element.setAttribute('fill', color);
                else if (barFiller) {
                    barFiller.style.backgroundColor = color;
                } else if (statusIndicator) {
                    statusIndicator.style.backgroundColor = color;
                } else if (valueInput) {
                    valueInput.style.color = color;
                }
            }
        } else {
            const currentFlashingColor = this.flashingAlarmsMap.get(elementId);
            let newFlashingColor;
            if (elementClasses['flag-cancel']) {
                if (currentFlashingColor != this.defaultColors[DefaultColorName.CANCEL].color) {
                    newFlashingColor = this.defaultColors[DefaultColorName.CANCEL].color;
                    this.flashingAlarmsMap.set(elementId, newFlashingColor);
                }
            } else if (elementClasses['flag-alarm']) {
                if (currentFlashingColor != this.defaultColors[DefaultColorName.ALARM].color) {
                    newFlashingColor = this.defaultColors[DefaultColorName.ALARM].color;
                    this.flashingAlarmsMap.set(elementId, newFlashingColor);
                }
            } else if (elementClasses['flag-caution']) {
                if (currentFlashingColor != this.defaultColors[DefaultColorName.CAUTION].color) {
                    newFlashingColor = this.defaultColors[DefaultColorName.CAUTION].color;
                    this.flashingAlarmsMap.set(elementId, newFlashingColor);
                }
            } else if (elementClasses['flag-warning']) {
                if (currentFlashingColor != this.defaultColors[DefaultColorName.WARNING].color) {
                    newFlashingColor = this.defaultColors[DefaultColorName.WARNING].color;
                    this.flashingAlarmsMap.set(elementId, newFlashingColor);
                }
            } else {
                newFlashingColor = this.defaultColors[DefaultColorName.NORMAL].color;
                this.flashingAlarmsMap.set(elementId, newFlashingColor);
            }
            if (newFlashingColor) {
                let ackColor = this.defaultColors[DefaultColorName.ACK].color;
                if (!barFiller && !statusIndicator && !valueInput) {
                    this.colorAnimationService.startFlashingSvg(newFlashingColor, ackColor, element, elementId);
                } else if (barFiller) {
                    this.colorAnimationService.startFlashingController(
                        newFlashingColor,
                        ackColor,
                        barFiller,
                        elementId
                    );
                } else if (statusIndicator) {
                    this.colorAnimationService.startFlashingController(
                        newFlashingColor,
                        ackColor,
                        statusIndicator,
                        elementId
                    );
                } else if (valueInput) {
                    this.colorAnimationService.startFlashingController(
                        newFlashingColor,
                        ackColor,
                        valueInput,
                        elementId,
                        'color'
                    );
                }
            }
        }
    }

    getDefaultColor() {
        return this.defaultColors[DefaultColorName.NORMAL].color;
    }

    getDefaultBarColor() {
        return this.defaultColors[DefaultColorName.BAR].color;
    }

    getDigitalContactChannelColor(channelValue) {
        if (channelValue === 1) {
            return this.defaultColors[DefaultColorName.DIGITAL_ON].color;
        }
        return this.defaultColors[DefaultColorName.DIGITAL_OFF].color;
    }

    getMotorChannelColor(value: number) {
        if (value === 1) {
            return this.defaultColors[DefaultColorName.MOTOR_RUNNING].color;
        }
        return this.defaultColors[DefaultColorName.MOTOR_STOP].color;
    }

    getValveColor(value: number) {
        if (value === 1) {
            return this.defaultColors[DefaultColorName.VALVE_OPEN].color;
        }
        return this.defaultColors[DefaultColorName.VALVE_CLOSE].color;
    }

    clear() {
        this.flashingAlarmsMap.clear();
        this.currentFills.clear();
    }

    getAlarmStateColor(alarmState, channel) {
        const defaultAlarmColor = this.defaultColors;

        if (alarmState === FlagOption.LOW) {
            return defaultAlarmColor[alarmLevelMap[channel.fcuData.alarmSettingLow.alarmLevel]];
        } else if (alarmState === FlagOption.LOW_LOW) {
            return defaultAlarmColor[alarmLevelMap[channel.fcuData.alarmSettingVeryLow.alarmLevel]];
        } else if (alarmState === FlagOption.HIGH) {
            return defaultAlarmColor[alarmLevelMap[channel.fcuData.alarmSettingHigh.alarmLevel]];
        } else if (alarmState === FlagOption.HIGH_HIGH) {
            return defaultAlarmColor[alarmLevelMap[channel.fcuData.alarmSettingVeryHigh.alarmLevel]];
        } else if (alarmState === FlagOption.DIGITAL) {
            return defaultAlarmColor[alarmLevelMap[channel.fcuData.digitalAlarmSetting.alarmLevel]];
        } else if (alarmState === FlagOption.FEEDBACK) {
            return defaultAlarmColor[alarmLevelMap[channel.fcuData.alarmSettingFeedbackError.alarmLevel]];
        } else if (alarmState === FlagOption.UNDER) {
            return defaultAlarmColor[DefaultColorName.SENSOR_FAILURE];
        } else if (alarmState === FlagOption.OVER) {
            return defaultAlarmColor[DefaultColorName.SENSOR_FAILURE];
        } else if (alarmState === FlagOption.UNDEFINED) {
            return {
                color: this.GRAPH_UNDEFINED_COLOR,
                systemColorId: this.GRAPH_UNDEFINED_COLOR,
            };
        } else {
            return (
                defaultAlarmColor[alarmState] || {
                    color: this.GRAPH_UNDEFINED_COLOR,
                    systemColorId: this.GRAPH_UNDEFINED_COLOR,
                }
            );
        }
    }
}

export const enum DefaultColorName {
    ALARM = 'Alarm',
    CAUTION = 'Caution',
    WARNING = 'Warning',
    ALARM_REPOSE = 'Alarm repose',
    SENSOR_FAILURE = 'Sensor failure',
    FU_UNRESPONSIVE = 'FU unresponsive',
    DIGITAL_ON = 'Digital on',
    DIGITAL_OFF = 'Digital off',
    MOTOR_RUNNING = 'Motor running',
    MOTOR_STOP = 'Motor stop',
    MOTOR_STANDBY = 'Motor standby',
    VALVE_OPEN = 'Valve open',
    VALVE_CLOSE = 'Valve close',
    ACK = 'ack',
    CANCEL = 'cancel',
    NORMAL = 'normal',
    BAR = 'bar',
}

enum AlarmLevel {
    Caution,
    Warning,
    Alarm,

    None,
}

const alarmLevelMap = {
    [AlarmLevel.Caution]: 'Caution',
    [AlarmLevel.Warning]: 'Warning',
    [AlarmLevel.Alarm]: 'Alarm',

    [AlarmLevel.None]: 'None',
};
